import React, { useState, useMemo, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';

// Données de l'équipe (inchangées)
const teamMembers = [
  {
    name: 'KAMBUA MBUKU Jack',
    role: 'Avocat',
    photo: 'images/pic/1.png',
    bio: 'Expert en droit des sociétés et contrats commerciaux, j’aide entreprises et entrepreneurs à sécuriser leurs opérations et anticiper leurs enjeux juridiques.',
    expertise: ['Gestion', 'Leadership', 'Stratégie']
  },
  {
    name: 'KILOLO DISASI Josué',
    role: 'Maître Avocat Associé',
    photo: 'images/pic/2.jpeg',
    bio: 'Expert en droit des sociétés et contrats commerciaux, j’aide entreprises et entrepreneurs à sécuriser leurs opérations et anticiper leurs enjeux juridiques.',
    expertise: ['Analyse', 'Surveillance', 'Prévention']
  },
  {
    name: 'EFAMBE YOLANGA Paul jr',
    role: 'Consultant International',
    photo: 'images/pic/4.jpeg',
    bio: 'Consultant en stratégie globale, j’accompagne organisations et gouvernements dans l’analyse, la planification et l’exécution de solutions à fort impact international.',
    expertise: ['Analyse', 'Surveillance', 'Prévention']
  },
  {
    name: 'BONONGA FREDY Ali',
    role: 'Maitre Avocat Collaborateur',
    photo: 'images/pic/3.jpeg',
    bio: 'Défenseur passionné des droits fondamentaux, j’accompagne chaque client avec rigueur, stratégie et détermination pour obtenir la justice qu’il mérite.',
    expertise: ['Droit Civil', 'Plaidoirie', 'Stratégie']
  },
  {
    name: 'ILUNGA KABAMBA Jeannot',
    role: 'Directeur des Ressources Humaines',
    photo: 'images/pic/5.jpeg',
    bio: 'Directeur RH engagé, j’accompagne les organisations dans la transformation, le développement des talents et la création d’un environnement de travail performant.',
    expertise: ['Ressources Humaines', 'Management', 'Talents']
  },
  {
    name: 'MAKIESE MABAYA Tichique',
    role: 'Secrétaire',
    photo: 'images/pic/6.jpeg',
    bio: 'Secrétaire organisée et polyvalente, je gère l’administratif, la communication interne et le soutien opérationnel pour assurer un fonctionnement fluide du cabinet.',
    expertise: ['Administration', 'Organisation', 'Communication']
  }
];

const TeamSection2 = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const cardsPerPage = 6; // Ajusté pour mieux remplir la grille avec 6 membres

  // Filtre combiné (nom, rôle, expertise)
  const filteredMembers = useMemo(() => {
    if (!searchTerm.trim()) return teamMembers;
    const lowerSearch = searchTerm.toLowerCase();
    return teamMembers.filter(
      (member) =>
        member.name.toLowerCase().includes(lowerSearch) ||
        member.role.toLowerCase().includes(lowerSearch) ||
        member.expertise.some(exp => exp.toLowerCase().includes(lowerSearch))
    );
  }, [searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredMembers.length / cardsPerPage);
  const indexOfLastCard = currentPage * cardsPerPage;
  const indexOfFirstCard = indexOfLastCard - cardsPerPage;
  const currentCards = filteredMembers.slice(indexOfFirstCard, indexOfLastCard);

  const goToPage = useCallback((page) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
  }, [totalPages]);

  // Gestion de l'erreur d'image
  const handleImageError = (e) => {
    e.target.src = 'https://placehold.co/600x800?text=Photo+non+disponible';
  };

  return (
    <Section>
      <NoiseOverlay />
      <GradientGlow />

      <HeaderContainer>
        <TitleLine />
        <SectionTitle>
          Notre <Highlight>Équipe.</Highlight>
        </SectionTitle>
        <IntroText>
          Composée d'avocats, de juristes et de consultants d'excellence, notre équipe 
          met son savoir-faire au service de vos intérêts stratégiques. Forts de plus de 145 
          interventions majeures, nous allions rigueur juridique et vision d'affaires.
        </IntroText>
      </HeaderContainer>

      <Filters>
        <FilterInputWrapper>
          <SearchIcon>🔍</SearchIcon>
          <FilterInput
            type="text"
            placeholder="Nom, rôle ou expertise..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Rechercher un membre"
          />
        </FilterInputWrapper>
      </Filters>

      {filteredMembers.length === 0 ? (
        <EmptyState>
          <EmptyIcon>⚖️</EmptyIcon>
          <EmptyMessage>Aucun membre ne correspond à votre recherche</EmptyMessage>
          <EmptyHint>Essayez un autre nom, rôle ou domaine d'expertise</EmptyHint>
        </EmptyState>
      ) : (
        <>
          <Grid>
            {currentCards.map((member, idx) => (
              <Card key={member.name} delay={idx * 0.05}>
                <ImageWrapper>
                  <ImageContainer>
                    <MemberImage 
                      src={member.photo} 
                      alt={member.name} 
                      onError={handleImageError}
                      loading="lazy"
                    />
                    <ImageOverlay />
                    <HoverOverlay />
                  </ImageContainer>
                </ImageWrapper>
                
                <Content>
                  <Name>{member.name}</Name>
                  <Role>{member.role}</Role>
                  
                  <Bio>
                    <BioText>{member.bio}</BioText>
                    <Expertise>
                      {member.expertise.map((item, i) => (
                        <ExpertiseItem key={i}>
                          <ExpertiseDot />
                          {item}
                        </ExpertiseItem>
                      ))}
                    </Expertise>
                  </Bio>
                </Content>
              </Card>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Pagination>
              <PageButton 
                onClick={() => goToPage(currentPage - 1)} 
                disabled={currentPage === 1}
                aria-label="Page précédente"
              >
                ←
              </PageButton>
              {Array.from({ length: totalPages }, (_, i) => (
                <PageButton
                  key={i}
                  active={i + 1 === currentPage}
                  onClick={() => goToPage(i + 1)}
                  aria-label={`Aller à la page ${i + 1}`}
                >
                  {i + 1}
                </PageButton>
              ))}
              <PageButton 
                onClick={() => goToPage(currentPage + 1)} 
                disabled={currentPage === totalPages}
                aria-label="Page suivante"
              >
                →
              </PageButton>
            </Pagination>
          )}
        </>
      )}
    </Section>
  );
};

// --- Animations ---
const cardEntrance = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// --- Palettes de couleurs enrichies ---
const colors = {
  bgDark: '#0B0B0D',
  bgCard: '#131316',
  beigeLight: '#F7F2EC',
  beigeMain: '#DCCFB8',
  beigeDark: '#9C8D77',
  textGray: '#A1A1A8',
  border: 'rgba(220, 207, 184, 0.12)',
  overlay: 'rgba(11, 11, 13, 0.75)',
};

// --- Styled Components ---
const Section = styled.section`
  padding: 6rem 5% 8rem;
  background: radial-gradient(ellipse at 20% 30%, #151518, ${colors.bgDark});
  position: relative;
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  min-height: 100vh;
`;

const NoiseOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.03'/%3E%3C/svg%3E");
  pointer-events: none;
  z-index: 1;
`;

const GradientGlow = styled.div`
  position: absolute;
  top: -20%; left: -20%;
  width: 800px; height: 800px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(220, 207, 184, 0.05) 0%, transparent 70%);
  filter: blur(100px);
  z-index: 0;
  pointer-events: none;
`;

const HeaderContainer = styled.div`
  text-align: center;
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto 4rem auto;
`;

const TitleLine = styled.div`
  width: 70px;
  height: 2px;
  background: linear-gradient(90deg, transparent, ${colors.beigeMain}, transparent);
  margin: 0 auto 2rem auto;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2.2rem, 6vw, 4rem);
  font-weight: 400;
  color: ${colors.beigeLight};
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
`;

const Highlight = styled.span`
  color: ${colors.beigeMain};
  font-style: italic;
  font-weight: 500;
`;

const IntroText = styled.p`
  color: ${colors.textGray};
  font-size: 1.1rem;
  line-height: 1.6;
  font-weight: 350;
  max-width: 700px;
  margin: 0 auto;
`;

const Filters = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 2;
`;

const FilterInputWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 450px;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1rem;
  color: ${colors.beigeDark};
  pointer-events: none;
  z-index: 2;
`;

const FilterInput = styled.input`
  width: 100%;
  padding: 0.9rem 1rem 0.9rem 2.8rem;
  border-radius: 40px;
  border: 1px solid ${colors.border};
  background: rgba(19, 19, 22, 0.7);
  backdrop-filter: blur(8px);
  color: ${colors.beigeLight};
  font-size: 0.95rem;
  transition: all 0.25s ease;
  outline: none;

  &::placeholder {
    color: #5a5a60;
    font-weight: 350;
  }

  &:focus {
    border-color: ${colors.beigeMain};
    background: rgba(19, 19, 22, 0.95);
    box-shadow: 0 0 0 2px rgba(220, 207, 184, 0.1);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Card = styled.div`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 2px;
  overflow: hidden;
  opacity: 0;
  animation: ${cardEntrance} 0.6s cubic-bezier(0.2, 0.9, 0.4, 1.1) forwards;
  animation-delay: ${props => props.delay || 0}s;
  transition: border-color 0.3s ease, transform 0.3s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: ${colors.beigeMain};
    transform: translateY(-5px);
    box-shadow: 0 20px 30px -15px rgba(0, 0, 0, 0.5);
    
    img {
      transform: scale(1.03);
      filter: grayscale(20%) contrast(1.1) brightness(0.9);
    }
  }
`;

const ImageWrapper = styled.div`
  padding: 12px 12px 0 12px;
`;

const ImageContainer = styled.div`
  position: relative;
  height: 400px;
  overflow: hidden;
  background: ${colors.bgDark};
  
  @media (max-width: 768px) {
    height: 340px;
  }
`;

const MemberImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  transition: transform 0.7s cubic-bezier(0.2, 0.9, 0.4, 1.1), filter 0.5s ease;
  filter: grayscale(100%) contrast(1.1) brightness(0.85);
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, ${colors.bgCard} 0%, transparent 50%);
  z-index: 1;
  pointer-events: none;
`;

const HoverOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${colors.overlay};
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
  pointer-events: none;
  
  ${Card}:hover & {
    opacity: 0.2;
  }
`;

const Content = styled.div`
  padding: 1.8rem;
  background: ${colors.bgCard};
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Name = styled.h3`
  font-size: 1.3rem;
  color: ${colors.beigeLight};
  margin: 0 0 0.4rem 0;
  font-weight: 500;
  letter-spacing: -0.3px;
  line-height: 1.3;
`;

const Role = styled.p`
  color: ${colors.beigeMain};
  font-size: 0.9rem;
  margin: 0;
  font-family: 'Playfair Display', 'Georgia', serif;
  font-style: italic;
  letter-spacing: 0.3px;
`;

const Bio = styled.div`
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition: max-height 0.5s ease-in-out, opacity 0.3s ease, margin 0.2s;
  margin-top: 0;
  
  ${Card}:hover & {
    max-height: 300px;
    opacity: 1;
    margin-top: 1.2rem;
  }
`;

const BioText = styled.p`
  color: ${colors.textGray};
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0 0 1rem 0;
  border-top: 1px solid ${colors.border};
  padding-top: 1rem;
`;

const Expertise = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 0.5rem;
`;

const ExpertiseItem = styled.span`
  color: ${colors.beigeLight};
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(220, 207, 184, 0.1);
  padding: 0.3rem 0.7rem;
  border-radius: 20px;
  letter-spacing: 0.3px;
`;

const ExpertiseDot = styled.div`
  width: 6px;
  height: 6px;
  background-color: ${colors.beigeMain};
  border-radius: 50%;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.6rem;
  margin-top: 5rem;
  position: relative;
  z-index: 2;
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  min-width: 40px;
  height: 40px;
  padding: 0 0.8rem;
  border: 1px solid ${props => props.active ? colors.beigeMain : colors.border};
  background: ${props => props.active ? colors.beigeMain : 'transparent'};
  color: ${props => props.active ? colors.bgDark : colors.textGray};
  border-radius: 2px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${props => props.active ? '600' : '400'};

  &:hover:not(:disabled) {
    border-color: ${colors.beigeMain};
    color: ${colors.beigeLight};
    background: rgba(220, 207, 184, 0.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: rgba(19, 19, 22, 0.6);
  border: 1px dashed ${colors.border};
  max-width: 500px;
  margin: 2rem auto;
  border-radius: 8px;
  z-index: 2;
  position: relative;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.7;
`;

const EmptyMessage = styled.p`
  color: ${colors.beigeLight};
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const EmptyHint = styled.p`
  color: ${colors.textGray};
  font-size: 0.9rem;
`;

export default TeamSection2;